import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema, { getWebsiteSchema, getWebPageSchema } from '@/components/feature/SEOSchema';
import { categories, articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

function CategoryCard({ category }: { category: typeof categories[0] }) {
  return (
    <Link
      to={`/category/${category.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={category.image}
          alt={category.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className={`${category.icon} text-primary-500`}></i>
          </div>
          <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
            {category.name}
          </h3>
        </div>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {category.description}
        </p>
      </div>
    </Link>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {article.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700"
            >
              {tag}
            </span>
          ))}
          <span className="text-xs text-foreground-400 ml-auto">{article.readTime} 分钟阅读</span>
        </div>
        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
        <div className="mt-3 flex items-center gap-1 text-xs text-foreground-400">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-calendar-line text-xs"></i>
          </div>
          <span>{article.date}</span>
        </div>
      </div>
    </Link>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [nlStatus, setNlStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [nlMessage, setNlMessage] = useState('');

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');

    if (honeypot && honeypot.value.trim() !== '') {
      setNlStatus('success');
      setNlMessage('订阅成功！感谢你的关注。');
      setEmail('');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9te4v1anrhn4bgf7ga0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, String(value)])
        ),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setNlStatus('success');
        setNlMessage('订阅成功！感谢你的关注。');
        setEmail('');
      } else {
        setNlStatus('error');
        setNlMessage('订阅失败，请稍后再试。');
      }
    } catch {
      setNlStatus('error');
      setNlMessage('网络错误，请稍后再试。');
    }

    setTimeout(() => { setNlStatus('idle'); setNlMessage(''); }, 5000);
  };

  return (
    <section className="bg-background-900 py-14 md:py-20">
      <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
          获取最新摄影技巧
        </h2>
        <p className="text-sm md:text-base text-background-400 mb-8">
          订阅我们的资讯，每周获取最新的偷拍自拍教程和摄影灵感
        </p>
        <form
          onSubmit={handleNewsletter}
          className="flex flex-col sm:flex-row gap-3 justify-center"
          data-readdy-form="true"
        >
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="输入你的邮箱地址"
            required
            className="px-5 py-3 rounded-full bg-background-800 text-background-100 text-sm placeholder:text-background-500 border border-background-700 focus:outline-none focus:border-primary-500 transition-colors w-full sm:w-80"
          />
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="honeypot-field"
          />
          <button
            type="submit"
            className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            订阅
          </button>
        </form>
        {nlStatus !== 'idle' && (
          <div className={`mt-4 text-sm px-4 py-3 rounded-lg ${nlStatus === 'success' ? 'bg-primary-500/20 text-primary-300' : 'bg-red-500/20 text-red-300'}`}>
            {nlMessage}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const [activeTab, setActiveTab] = useState('all');
  const tabCategories = [
    { slug: 'all', name: '全部' },
    ...categories,
  ];

  const filteredArticles = activeTab === 'all'
    ? articles
    : articles.filter((a) => a.category === activeTab);

  const webPageSchema = getWebPageSchema(
    '偷拍自拍技巧大全 - 首页',
    '偷拍自拍技巧大全——分享最实用的自然感自拍方法、手机自拍构图技巧、光线运用秘诀及后期处理教程。涵盖构图技巧、设备推荐、光线运用、后期处理等全面内容。',
    'https://999vv.cn/'
  );

  return (
    <>
      <SEOSchema type="website" />
      <SEOSchema type="organization" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Candid%20selfie%20photography%20aesthetic%20with%20warm%20natural%20light%2C%20soft%20dreamy%20atmosphere%2C%20lifestyle%20portrait%20in%20golden%20hour%2C%20artistic%20mood%2C%20clean%20minimal%20composition%2C%20neutral%20warm%20tones%2C%20organic%20texture%2C%20cinematic%20feel&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
            alt="偷拍自拍技巧大全"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              拍出<span className="text-primary-400">自然真实</span>的每一个瞬间
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              偷拍自拍是当下最流行的摄影风格之一，追求自然、不刻意的真实感画面。
              掌握构图技巧、光线运用与后期处理，轻松拍出最有故事感的日常瞬间。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/composition"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                开始学习
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/15 text-white font-medium text-sm hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-white/60 text-xl"></i>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-100 border-y border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{siteInfo.stats.articles}+</div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">精选教程</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{(siteInfo.stats.readers / 10000).toFixed(1)}万+</div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">摄影爱好者</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{siteInfo.stats.categories}</div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">分类专题</div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              探索摄影分类
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              从构图到后期，六大分类助你系统掌握偷拍自拍的每一个环节
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <CategoryCard key={cat.slug} category={cat} />
            ))}
          </div>
        </section>

        {/* Featured Articles Section */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                精选推荐
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                最受欢迎的偷拍自拍教程，助你快速上手
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center justify-center mb-8 overflow-x-auto pb-2">
              <div className="inline-flex items-center bg-background-200/50 rounded-full px-1 py-1">
                {tabCategories.map((tab) => (
                  <button
                    key={tab.slug}
                    onClick={() => setActiveTab(tab.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.slug
                        ? 'bg-background-50 text-primary-600'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/category/composition"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-500 text-primary-600 font-medium text-sm hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                查看全部教程
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              为什么选择我们
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              不只是教程，更是一种摄影生活方式的分享
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center px-4">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-book-open-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">系统化教学</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                从零基础到进阶，六大分类系统覆盖偷拍自拍的全部环节，循序渐进帮你提升摄影水平。
              </p>
            </div>
            <div className="text-center px-4">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-smartphone-line text-2xl text-accent-500"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">实用至上</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                所有技巧都可以用手机实现，无需昂贵设备。每个教程都有具体的操作步骤和实战案例。
              </p>
            </div>
            <div className="text-center px-4">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-heart-line text-2xl text-secondary-500"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">真实自然</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                拒绝过度摆拍和千篇一律的滤镜，我们追求的是"不经意的好看"和真实的日常美感。
              </p>
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <NewsletterSection />

        {/* About Snippet */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="bg-background-100 rounded-lg border border-background-200/60 p-8 md:p-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              关于偷拍自拍技巧大全
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-6 leading-relaxed">
              我们是一群热爱摄影的创作者，坚信最好的照片不是"摆"出来的，而是捕捉到的。
              偷拍自拍技巧大全汇集了从构图到后期、从设备到场景的全方位教程，
              帮助每一位摄影爱好者用镜头记录生活中最真实、最动人的瞬间。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              了解更多关于我们
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}